import React, { FC, useEffect, useReducer, useState } from 'react'
import { LinearProgress, Paper, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useContractorTransferPreviewRequest, useNewContractorTransferRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { Entities } from 'src/enums'
import { getTransferQueuedMessage } from 'src/components/columns/contractorTransferHelpers'
import { ContractorsStep } from './contractorsStep'
import { DevicesStep } from './devicesStep'
import { ProjectsStep } from './projectsStep'
import { SummaryStep } from './summaryStep'
import { UsersStep } from './usersStep'
import {
  buildTransferRequest,
  hasTransferContent,
  hasWizardSelection,
  initialTransferWizardState,
  TRANSFER_WIZARD_STEPS,
  transferWizardReducer,
  TransferWizardStep
} from './transferWizardHelpers'

// lists whose rows a transfer moves to another contractor
const INVALIDATED_ENTITIES = [Entities.Contractors, Entities.Projects, Entities.Users, Entities.UserContractors, Entities.Sensors, Entities.Gateway, Entities.ContractorTransfers]

/**
 * The transfer wizard. The selection lives in this component only: it can reach thousands of ids, nothing is pre-selected
 * through the URL, and a reload starts over (the browser asks first once something is picked).
 */
export const ContractorTransferWizard: FC = () => {
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addMessage = useAddPopupMessage()
  const [state, dispatch] = useReducer(transferWizardReducer, initialTransferWizardState)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (submitted || !hasWizardSelection(state)) return undefined

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)

    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [state, submitted])

  useEffect(() => {
    window.scrollTo?.(0, 0)
  }, [state.step])

  // the projects that move are derived from the selected users; one preview serves the projects and devices steps. They
  // show only what moves, so it is the plan alone: the row counts and the readings estimate wait for the summary
  const usersRequest = buildTransferRequest({ ...state, extraSensorIds: [], extraGatewayIds: [] })
  const usersPreview = useContractorTransferPreviewRequest({
    payload: usersRequest ?? ({} as never),
    planOnly: true,
    options: {
      enabled: Boolean(usersRequest) && state.userContractorIds.length > 0 && state.step >= TransferWizardStep.Projects,
      keepPreviousData: true,
      retry: false
    }
  })
  const movingPreview = state.userContractorIds.length ? usersPreview.data : undefined
  const movingProjectIds = movingPreview?.projects.map((project) => project.id) ?? []

  // the summary previews exactly what Run sends, projects included
  const fullRequest = buildTransferRequest(state, movingProjectIds)
  const summaryPreview = useContractorTransferPreviewRequest({
    payload: fullRequest ?? ({} as never),
    options: {
      enabled: Boolean(fullRequest) && hasTransferContent(state) && state.step === TransferWizardStep.Summary && !usersPreview.isFetching,
      retry: false
    }
  })

  const create = useNewContractorTransferRequest({
    options: {
      onSuccess: (transfer) => {
        setSubmitted(true)
        setConfirmOpen(false)
        addMessage(getTransferQueuedMessage(transfer))
        navigate(`/settings/transfers/${transfer.id}`)
        // the previews of this wizard are still mounted here; refetching them would only repeat the impact report
        INVALIDATED_ENTITIES.forEach((entity) => queryClient.invalidateQueries({ queryKey: [entity], predicate: ({ queryKey }) => queryKey[1] !== 'preview' }))
      },
      onError: ({ message: text }) => {
        setConfirmOpen(false)
        addMessage({ text, type: 'error' })
        // the conflicts of a 422 are not in the error; a fresh preview shows them
        summaryPreview.refetch()
      }
    }
  })

  const renderStep = (): React.ReactElement => {
    switch (state.step) {
      case TransferWizardStep.Contractors:
        return <ContractorsStep state={state} dispatch={dispatch} />
      case TransferWizardStep.Users:
        return <UsersStep state={state} dispatch={dispatch} />
      case TransferWizardStep.Projects:
        return (
          <ProjectsStep
            state={state}
            dispatch={dispatch}
            preview={movingPreview}
            isPreviewLoading={state.userContractorIds.length > 0 && usersPreview.isFetching}
            previewError={state.userContractorIds.length ? usersPreview.error?.message : undefined}
          />
        )
      case TransferWizardStep.Devices:
        return <DevicesStep state={state} dispatch={dispatch} preview={movingPreview} isPreviewLoading={state.userContractorIds.length > 0 && usersPreview.isFetching} />
      default:
        return (
          <SummaryStep
            state={state}
            dispatch={dispatch}
            preview={summaryPreview.data}
            isPreviewLoading={summaryPreview.isFetching || usersPreview.isFetching}
            previewError={summaryPreview.error?.message}
            confirmOpen={confirmOpen}
            onOpenConfirm={() => setConfirmOpen(true)}
            onCloseConfirm={() => setConfirmOpen(false)}
            onRun={() => fullRequest && create.mutate(fullRequest)}
            isRunning={create.isLoading}
            onCheckAgain={() => summaryPreview.refetch()}
          />
        )
    }
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        {isPhone ? (
          // five stacked labels would fill the first phone screen
          <>
            <Typography variant="subtitle2" mb={1}>{`Step ${state.step + 1} of ${TRANSFER_WIZARD_STEPS.length}: ${TRANSFER_WIZARD_STEPS[state.step]}`}</Typography>
            <LinearProgress variant="determinate" value={((state.step + 1) / TRANSFER_WIZARD_STEPS.length) * 100} />
          </>
        ) : (
          <Stepper activeStep={state.step} alternativeLabel>
            {TRANSFER_WIZARD_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Paper>
      {renderStep()}
    </>
  )
}
